import { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FormContainer from '../components/FormContainer';
import { toast } from 'react-toastify';
import { useUpdateProfileMutation, useGetUserProfileQuery } from '../slices/usersApiSlice';
import { setCredentials } from '../slices/authSlice';
import Loader from '../components/Loader';

const ProfileScreen = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [trackitUsername, setTrackitUsername] = useState('');
  const [trackitPassword, setTrackitPassword] = useState('');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userInfo } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
    }
  }, [userInfo, navigate]);

  const { data: profile, isLoading, refetch } = useGetUserProfileQuery(undefined, {
    skip: !userInfo
  });
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

  useEffect(() => {
    if (profile) {
      setName(profile.name);
      setEmail(profile.email);
      let tUsername = profile.trackitUsername || '';
      if (tUsername.toLowerCase().startsWith('help desk\\vhs1\\')) {
        tUsername = tUsername.substring(15);
      }
      setTrackitUsername(tUsername);
    }
  }, [profile]);

  const submitAppProfileHandler = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      const res = await updateProfile({
        name,
        email,
        password: password ? password : undefined,
      }).unwrap();
      
      dispatch(setCredentials({ ...res }));
      toast.success('App Profile updated successfully');
      setPassword('');
      setConfirmPassword('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Something went wrong');
    }
  };

  const submitTrackitHandler = async (e) => {
    e.preventDefault();
    
    try {
      const res = await updateProfile({
        trackitUsername,
        trackitPassword: trackitPassword ? trackitPassword : undefined,
      }).unwrap();
      
      dispatch(setCredentials({ ...res }));
      toast.success('Track-It Credentials updated successfully');
      setTrackitPassword('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Something went wrong');
    }
  };

  if (!userInfo) {
    return null;
  }

  return (
    <FormContainer>
      <h1>User Profile</h1>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Form onSubmit={submitAppProfileHandler}>
            <Form.Group controlId='name' className='my-3'>
              <Form.Label>Name</Form.Label>
              <Form.Control
                type='text'
                placeholder='Enter name'
                value={name}
                onChange={(e) => setName(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId='email' className='my-3'>
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type='email'
                placeholder='Enter email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId='password' className='my-3'>
              <Form.Label>Password</Form.Label>
              <Form.Control
                type='password'
                placeholder='Enter new password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId='confirmPassword' className='my-3'>
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type='password'
                placeholder='Confirm new password'
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Button type='submit' variant='primary' className='mt-2' disabled={isUpdating}>
              {isUpdating ? 'Updating...' : 'Update App Profile'}
            </Button>
          </Form>

          <hr className="my-5" />
          
          <h4 className="d-flex align-items-center gap-2">
            Track-It Credentials (Optional)
            {profile?.trackitUsername && (
              <Badge bg="success" pill style={{ fontSize: '0.45em' }} title="Using personal account for API updates">
                <i className="bi bi-check-circle-fill me-1"></i> Authenticated
              </Badge>
            )}
          </h4>
          <p className="text-muted small">Link your Track-It account to perform actions as yourself instead of the system admin.</p>

          <Form onSubmit={submitTrackitHandler}>
            <Form.Group controlId='trackitUsername' className='my-3'>
              <Form.Label>Track-It Username</Form.Label>
              <Form.Control
                type='text'
                placeholder='Enter Track-It Username (e.g. Help Desk\jredmond)'
                value={trackitUsername}
                onChange={(e) => setTrackitUsername(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Form.Group controlId='trackitPassword' className='my-3'>
              <Form.Label>Track-It Password</Form.Label>
              <Form.Control
                type='password'
                placeholder='Enter Track-It Password (to update)'
                value={trackitPassword}
                onChange={(e) => setTrackitPassword(e.target.value)}
              ></Form.Control>
            </Form.Group>

            <Button type='submit' variant='primary' className='mt-2' disabled={isUpdating}>
              {isUpdating ? 'Updating...' : 'Update Track-It Login'}
            </Button>
          </Form>
        </>
      )}
    </FormContainer>
  );
};

export default ProfileScreen;
